'use client';
import {
  faLocationDot,
  faMagnifyingGlass,
} from '@fortawesome/pro-light-svg-icons';
import { useEffect, useState } from 'react';
import { useStore } from 'zustand';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { useRouter } from 'next/navigation';

import { InitialLocationSuggestions } from './initial-location-suggestions.component';
import { LocationSearchResults } from './location-search-results.component';
import { RecentSearchedLocations } from './recent-searched-locations.component';
import { RegisterAddress } from './register-address.component';

import { useGoogleMaps } from '@/app/(main)/(pages)/(home)/_components/location/_hooks/use-google-maps.hook';
import { Button } from '@/components/ui/button/button.component';
import { Input } from '@/components/ui/input/input.component';
import { Modal } from '@/components/ui/modal.component';
import { useDebounceFn } from '@/hooks/use-debounce.hook';
import { useLoading } from '@/hooks/use-loading.hook';
import { authStore } from '@/store/auth.store';
import {
  modalsOpeningStore,
  setOpenBloquedGeolocationModal,
  setOpenLocationSelectionModal,
} from '@/store/modal';
import {
  addRecentSearch,
  clearRecentSearches,
  resetUserLocation,
  setUserLocation,
  userLocationStore,
} from '@/store/user-location.store';
import useIsMobile from '@/hooks/use-is-mobile.hook';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Icon } from '@/components/ui/icon.component';
import { useFocus } from '@/hooks/use-focus.hook';
import { AutocompletePrediction, Place } from '@/types/google-maps.types';
import { getCitySlug } from '@/app/(main)/(pages)/_services/get-city-slug.service';
import { useSearchParams } from '@/hooks/use-search-params.hook';
import { PersistedUserNavigationSearchParams } from '@/types/enums/persisted-user-navigation-search-params.enum';
import { slugify } from '@/utils/formatters/slugfy';
import { openSearchModal } from '@/store/search/search.store';
import { buildUrlWithPersistedParams } from '@/utils/persisted-params.util';
import { getLogger } from '@/logger/get-logger';
import { TrackUserView } from '@/app/(main)/_components/track-user-view/track-user-view.component';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { branchesListId } from '@/app/(main)/(pages)/_components/branches-section/consts/branches-section.const';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';

export const LocationSelectionModal = () => {
  const router = useRouter();
  const searchParams = useSearchParams([...Object.values(PersistedUserNavigationSearchParams)]);

  const {
    getGoogleMapsPredictions,
    getGoogleMapsPlaceDetailsWithGeocoder,
    getGoogleMapsPlaceByCoordinates,
  } = useGoogleMaps();

  const analytics = getAnalyticsPlugin();

  const [searchDisplayValue, setSearchDisplayValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<AutocompletePrediction[]>(
    [],
  );
  const [shouldShowEmptyResultsMessage, setShouldShowEmptyResultsMessage]
    = useState(false);

  const { isLoading, startLoading, stopLoading } = useLoading();
  const isMobile = useIsMobile();
  const { htmlElRef, setFocus } = useFocus();

  const isLoggedIn = useStore(authStore, (state) => state.computed.isLoggedIn);
  const recentSearches = useStore(
    userLocationStore,
    (state) => state.recentSearches,
  );
  const {
    open,
    locationCallback,
    pendingSearchTerm,
    shouldOpenSearchModal,
    shouldScrollToBranchesListOnNavigate,
  } = useStore(modalsOpeningStore, (state) => ({
    open: state?.locationSelectionModalIsOpen,
    locationCallback: state?.locationCallback,
    pendingSearchTerm: state?.pendingSearchTerm,
    shouldOpenSearchModal: state?.shouldOpenSearchModal,
    shouldScrollToBranchesListOnNavigate: state?.shouldScrollToBranchesListOnNavigate,
  }));

  const debouncedChange = useDebounceFn(setSearchValue, 500);

  useEffect(() => {
    async function handlePredictions() {
      startLoading();

      const predictions = await getGoogleMapsPredictions(searchValue);

      setShouldShowEmptyResultsMessage(!predictions?.length);

      setSearchResults(predictions);

      stopLoading();
    }

    if (searchValue.length > 2) {
      handlePredictions();
    } else {
      setShouldShowEmptyResultsMessage(false);
      setSearchResults([]);
    }
  }, [searchValue]);

  async function clearUserLocationValuesInLogout() {
    setSearchDisplayValue('');
    setSearchResults([]);
    setSearchValue('');
    setShouldShowEmptyResultsMessage(false);
    resetUserLocation();
    clearRecentSearches();
  }

  useEffect(() => {
    clearUserLocationValuesInLogout();
  }, [isLoggedIn]);

  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    analytics.track(AnalyticsEnum.SEARCH_FOR_LOCATION, {
      termo: e.target.value,
    });
    debouncedChange(e.target.value);
    setSearchDisplayValue(e.target.value);
  };

  const navigateToCityPage = async (place: Place): Promise<void> => {
    const city = await getCitySlug(place.city, place.state);

    if (!city?.slug) {
      return;
    }

    const baseUrl = `/cidades/${city.slug}`;
    const link = buildUrlWithPersistedParams({
      baseUrl,
      overrides: {
        [PersistedUserNavigationSearchParams.CIDADE]: city.slug,
      },
    });

    const finalLink = shouldScrollToBranchesListOnNavigate
      ? `${link}#${branchesListId}`
      : link;

    await router.push(finalLink);
  };

  const useMyLocation = async (): Promise<void> => {
    analytics.track(AnalyticsEnum.USE_MY_LOCATION);

    try {
      const location = await navigator.permissions.query({
        name: 'geolocation',
      });
      if (location.state === 'denied') {
        setLocationAsDenied();
      }

      const place = await getGoogleMapsPlaceByCoordinates();

      const userLocation = {
        formattedAddress: place.formattedAddress,
        latitude: place.latitude,
        longitude: place.longitude,
        types: place.types,
        city: place.city,
        state: place.state,
        street: place.street,
        placeId: place.placeId,
        locationType: place.locationType,
      };

      setUserLocation(userLocation);
      searchParams.setParams({
        [PersistedUserNavigationSearchParams.CIDADE]: slugify(
          `${userLocation.city}-${userLocation.state}`,
        ),
      });

      closeLocationModal();

      if (locationCallback) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        locationCallback(userLocation);
        if (pendingSearchTerm !== undefined) {
          openSearchModal(true, pendingSearchTerm);
        }
      } else {
        await navigateToCityPage(place);
        if (pendingSearchTerm !== undefined) {
          openSearchModal(true, pendingSearchTerm);
        } else if (shouldOpenSearchModal) {
          openSearchModal(true);
        }
      }
    } catch (error) {
      const logger = getLogger('LocationSelectionModal', {});
      logger.error('Error using geolocation', { error });
      setLocationAsDenied();
    }
  };

  const setLocationAsDenied = async () => {
    setOpenLocationSelectionModal(false);
    setOpenBloquedGeolocationModal(true);
    stopLoading();
  };

  const closeLocationModal = () => {
    setOpenLocationSelectionModal(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpenLocationSelectionModal(isOpen);
  };

  const handleLocationClick = async (
    autocompletePrediction: AutocompletePrediction,
  ) => {
    analytics.track(AnalyticsEnum.CLICK_SEARCH_RESULT_CITY, {
      cidade: autocompletePrediction.description,
    });

    let predictions = [] as AutocompletePrediction[];
    if (!autocompletePrediction.placeId) {
      predictions = await getGoogleMapsPredictions(
        autocompletePrediction.description,
      );
    }
    const place = await getGoogleMapsPlaceDetailsWithGeocoder(
      predictions.length
        ? predictions[0].placeId
        : autocompletePrediction.placeId,
    );

    setUserLocation(place);
    searchParams.setParams({
      [PersistedUserNavigationSearchParams.CIDADE]: slugify(
        `${place.city}-${place.state}`,
      ),
    });

    closeLocationModal();

    addRecentSearch(autocompletePrediction);

    if (locationCallback) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      locationCallback(place);
      if (pendingSearchTerm !== undefined) {
        openSearchModal(true, pendingSearchTerm);
      }
    } else {
      await navigateToCityPage(place);
      if (pendingSearchTerm !== undefined) {
        openSearchModal(true, pendingSearchTerm);
      } else if (shouldOpenSearchModal) {
        openSearchModal(true);
      }
    }
  };

  const handleLocationClickFromSuggestions = async (
    item: Place,
  ) => {
    analytics.track(AnalyticsEnum.CLICK_MOST_SEARCHED_CITY, {
      cidade: item.city,
    });

    setUserLocation(item);
    searchParams.setParams({
      [PersistedUserNavigationSearchParams.CIDADE]: slugify(
        `${item.city}-${item.state}`,
      ),
    });

    closeLocationModal();

    if (locationCallback) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      locationCallback(item);
      if (pendingSearchTerm !== undefined) {
        openSearchModal(true, pendingSearchTerm);
      }
    } else {
      await navigateToCityPage(item);
      if (pendingSearchTerm !== undefined) {
        openSearchModal(true, pendingSearchTerm);
      } else if (shouldOpenSearchModal) {
        openSearchModal(true);
      }
    }
  };

  const resetSearchResults = () => {
    setSearchResults([]);
  };

  useEffect(() => {
    if (open) {
      setFocus();
    }
  }, [open]);

  const hasPredictions = searchResults.length > 0;
  const shouldShowRecentSearched
    = recentSearches.length > 0 && !searchDisplayValue;
  const shouldShowRegisterAddress = !searchDisplayValue && isLoggedIn;
  const shouldShowLocationSearchResults
    = !shouldShowRecentSearched && hasPredictions;
  const shouldShowInitialSuggestions
    = !shouldShowRecentSearched
      && !searchDisplayValue
      && !hasPredictions
      && !isLoggedIn;
  const shouldShowEmptyResults
    = !hasPredictions
      && searchDisplayValue.length > 2
      && shouldShowEmptyResultsMessage;

  return (
    <>
      {open && <TrackUserView eventName={AnalyticsEnum.VIEW_LOCATION_MODAL} />}

      {
        isMobile
          ? (
              <Sheet open={open} onOpenChange={handleOpenChange}>
                <SheetContent
                  side="bottom"
                  closingTagPosition="right"
                  className="w-full p-0 h-full"
                  hasHeader={true}
                >
                  <SheetHeader>
                    <div className="flex justify-between gap-[10px] p-6 px-5 pb-0 items-center ">
                      <SheetTitle>
                        <div className="flex items-center gap-[10px]">
                          <Icon
                            icon={faLocationDot}
                            size="xl"
                            className="text-green-400"
                          />
                          <span className="font-bold text-2xl text-gray-900 text-nowrap">
                            Local de atendimento
                          </span>
                        </div>
                      </SheetTitle>
                      <SheetClose
                        asChild
                        className="text-gray-500 cursor-pointer self-center px-2 hover:bg-gray-50"
                      >
                        <Icon
                          data-testid={DataTestIdEnum.LOCATION_SELECTION_MODAL_CLOSE_BUTTON}
                          icon={faTimes}
                          size="xl"
                          className="hover:text-gray-900 text-[1.625rem] "
                        />
                      </SheetClose>
                    </div>
                  </SheetHeader>
                  <div
                    className="flex flex-col gap-6 px-5 pb-6"
                    data-testid={DataTestIdEnum.LOCATION_SELECTION_MODAL}
                  >
                    <p className="text-base leading-6 text-gray-700 font-normal mt-6">
                      Queremos garantir seu acesso às melhores ofertas de saúde na região.
                    </p>
                    <div className="flex flex-col gap-4 mt-0">
                      <Button
                        startIcon={faLocationDot}
                        color="primary"
                        variant="default"
                        onClick={useMyLocation}
                      >
                        Usar a minha localização
                      </Button>
                      <div className="flex flex-col items-start">
                        <Input
                          startIconStyle="text-green-500"
                          startIcon={faMagnifyingGlass}
                          placeholder="Busque pela cidade, bairro ou rua"
                          onChange={handleSearchInput}
                          value={searchDisplayValue}
                          ref={htmlElRef}
                          data-testid={DataTestIdEnum.HOME_LOCATION_SEARCH_INPUT}
                        />
                        {searchDisplayValue.length > 0
                          && searchDisplayValue.length < 3 && (
                          <span className="text-sm text-gray-500 ">
                            Digite pelo menos 3 caracteres
                          </span>
                        )}
                      </div>
                    </div>
                    {isLoading
                      ? (
                          <div className="loading loading-lg my-4"></div>
                        )
                      : (
                          <div>
                            {shouldShowRegisterAddress && (
                              <RegisterAddress handleLocationClick={handleLocationClick} />
                            )}
                            <div className="flex flex-col items-start justify-center gap-4">
                              {shouldShowRecentSearched && (
                                <RecentSearchedLocations
                                  handleLocationClick={handleLocationClick}
                                  callback={resetSearchResults}
                                />
                              )}
                              {shouldShowLocationSearchResults && (
                                <LocationSearchResults
                                  searchResults={searchResults}
                                  handleLocationClick={handleLocationClick}
                                />
                              )}
                              {shouldShowInitialSuggestions && (
                                <InitialLocationSuggestions
                                  handleLocationClick={handleLocationClickFromSuggestions}
                                />
                              )}
                              {shouldShowEmptyResults && (
                                <p>Sem resultados para essa busca.</p>
                              )}
                            </div>
                          </div>
                        )}
                  </div>
                </SheetContent>
              </Sheet>
            )
          : (
              <Modal open={open} onOpenChange={handleOpenChange}>
                <Modal.Content
                  title="Local de atendimento"
                  className="bg-white md:max-w-[50rem] md:min-h-[32rem]"
                  icon={faLocationDot}
                  iconStyle="text-green-400"
                >
                  <div
                    className="flex flex-col gap-6 px-5 pb-6"
                    data-testid={DataTestIdEnum.LOCATION_SELECTION_MODAL}
                  >
                    <p className="text-base leading-6 text-gray-700 font-normal mt-6">
                      Queremos garantir seu acesso às melhores ofertas de saúde na região.
                    </p>
                    <div className="flex flex-col gap-4 mt-0">
                      <Button
                        startIcon={faLocationDot}
                        color="primary"
                        variant="default"
                        onClick={useMyLocation}
                      >
                        Usar a minha localização
                      </Button>
                      <div className="flex flex-col items-start">
                        <Input
                          startIconStyle="text-green-500"
                          startIcon={faMagnifyingGlass}
                          placeholder="Busque pela cidade, bairro ou rua"
                          onChange={handleSearchInput}
                          value={searchDisplayValue}
                          ref={htmlElRef}
                          data-testid={DataTestIdEnum.HOME_LOCATION_SEARCH_INPUT}
                        />
                        {searchDisplayValue.length > 0
                          && searchDisplayValue.length < 3 && (
                          <span className="text-sm text-gray-500 ">
                            Digite pelo menos 3 caracteres
                          </span>
                        )}
                      </div>
                    </div>

                    {isLoading
                      ? (
                          <div className="loading loading-lg my-4"></div>
                        )
                      : (
                          <div>
                            {shouldShowRegisterAddress && (
                              <RegisterAddress handleLocationClick={handleLocationClick} />
                            )}
                            <div className="flex flex-col items-start justify-center gap-4">
                              {shouldShowRecentSearched && (
                                <RecentSearchedLocations
                                  handleLocationClick={handleLocationClick}
                                  callback={resetSearchResults}
                                />
                              )}
                              {shouldShowLocationSearchResults && (
                                <LocationSearchResults
                                  searchResults={searchResults}
                                  handleLocationClick={handleLocationClick}
                                />
                              )}
                              {shouldShowInitialSuggestions && (
                                <InitialLocationSuggestions
                                  handleLocationClick={handleLocationClickFromSuggestions}
                                />
                              )}
                              {shouldShowEmptyResults && (
                                <p>Sem resultados para essa busca.</p>
                              )}
                            </div>
                          </div>
                        )}
                  </div>
                </Modal.Content>
              </Modal>
            )
      }
    </>
  );
};
